import React from 'react';
import { Link } from 'react-router-dom';

const TaxToolsInfoPage: React.FC = () => {
  return (
    <div className="income-tax-info-page">
      <div className="hero-section tax">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Canadian Tax & Payroll Tools - Income Tax, CPP, EI & Payroll Deductions</h1>
            <p className="hero-description">
              Estimate your Canadian income tax, calculate payroll deductions, and see CPP/EI breakdowns
              for all provinces and territories. Updated for 2024 and 2025 tax years.
            </p>
            <div className="cta-buttons">
              <Link to="/tax-tools" className="btn btn-primary btn-large">Open Tax Tools</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>What's Included</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Income Tax Estimator</h3>
              <p>Calculate federal and provincial income tax for any province. See your marginal rate, effective rate, and take-home pay at a glance.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📋</div>
              <h3>Payroll Deductions</h3>
              <p>Compute per-pay-period deductions: federal tax, provincial tax, CPP, and EI. Supports weekly, biweekly, semi-monthly, and monthly pay cycles.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🍁</div>
              <h3>All Provinces & Territories</h3>
              <p>Accurate 2024/2025 tax brackets for Ontario, BC, Alberta, Quebec, and every other province and territory in Canada.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🧮</div>
              <h3>CPP & CPP2 Calculations</h3>
              <p>Full CPP base and CPP2 (enhanced) contribution calculations for employees and self-employed, including YMPE and YAMPE thresholds.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>EI Premium Breakdown</h3>
              <p>Employment Insurance premiums with annual maximums. See how EI is deducted each pay period and when you hit the yearly cap.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💼</div>
              <h3>Self-Employment Support</h3>
              <p>Calculate both employee and employer CPP portions for self-employed individuals. EI is optional for self-employed workers.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="guide-section">
        <div className="container">
          <h2>Source Data & Official References</h2>
          <div className="guide-content">
            <p>Our calculations are based on publicly available rates published by the Canada Revenue Agency (CRA) and provincial governments. Key sources include:</p>
            <ul>
              <li>
                <strong>CRA T4127 Payroll Deductions Tables</strong> &mdash; Official payroll deduction formulas used to compute federal and provincial tax, CPP, and EI withholdings per pay period.{' '}
                <a href="https://www.canada.ca/en/revenue-agency/services/forms-publications/payroll/t4127-payroll-deductions-formulas.html" target="_blank" rel="noopener noreferrer">canada.ca/t4127</a>
              </li>
              <li>
                <strong>CPP Contribution Rates & Maximums</strong> &mdash; Employee rate 5.95%, self-employed 11.9%. YMPE $68,500 (2024), YAMPE $73,200 (2024). CPP2 rate 4% on earnings between YMPE and YAMPE.{' '}
                <a href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/payroll/payroll-deductions-contributions/canada-pension-plan-cpp.html" target="_blank" rel="noopener noreferrer">CRA CPP rates</a>
              </li>
              <li>
                <strong>EI Premium Rates & Maximums</strong> &mdash; Employee rate 1.66%, maximum insurable earnings $63,200 (2024), maximum annual premium $1,049.12.{' '}
                <a href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/payroll/payroll-deductions-contributions/employment-insurance-ei.html" target="_blank" rel="noopener noreferrer">CRA EI rates</a>
              </li>
              <li>
                <strong>Federal Tax Brackets</strong> &mdash; Five brackets from 15% to 33%. Basic personal amount $15,705 (2024).{' '}
                <a href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/frequently-asked-questions-individuals/canadian-income-tax-rates-individuals-current-previous-years.html" target="_blank" rel="noopener noreferrer">CRA federal rates</a>
              </li>
              <li>
                <strong>Provincial & Territorial Tax Brackets</strong> &mdash; Each jurisdiction publishes its own rates and surtaxes. We include all 13 provinces and territories with their specific basic personal amounts and bracket structures.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Features by Tier</h2>
          <div className="pricing-grid">
            <div className="pricing-card free">
              <h3>Free Calculator</h3>
              <div className="price">$0</div>
              <ul>
                <li>Unlimited calculations</li>
                <li>Income Tax Estimator</li>
                <li>Payroll Deductions Calculator</li>
                <li>All provinces and territories</li>
                <li>CPP, CPP2, and EI breakdowns</li>
                <li>Marginal rate analysis</li>
              </ul>
              <Link to="/tax-tools" className="btn btn-primary">Calculate Free</Link>
            </div>
            <div className="pricing-card account">
              <h3>Account Features</h3>
              <div className="price">Free</div>
              <ul>
                <li>Save your calculations</li>
                <li>Compare scenarios</li>
                <li>Access other calculators</li>
                <li>Retirement planner</li>
                <li>Housing calculator</li>
              </ul>
              <Link to="/account" className="btn btn-secondary">Create Account</Link>
            </div>
            <div className="pricing-card supporter">
              <h3>Supporters Club</h3>
              <div className="price">$10/year</div>
              <ul>
                <li>Everything in Free</li>
                <li>Unlimited saved scenarios</li>
                <li>Priority support</li>
                <li>Early access to features</li>
                <li>Support Canadian development</li>
              </ul>
              <Link to="/account" className="btn btn-premium">Join Supporters</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="disclaimer-section">
        <div className="container">
          <h2>Important Disclaimer</h2>
          <div className="disclaimer-content">
            <p><strong>These calculations are illustrative estimates only</strong> and should not be relied upon for tax filing or payroll processing.</p>
            <p>Always verify with official CRA payroll deduction tables (T4127) and consult a qualified tax professional for actual tax filings. Rates and thresholds may change &mdash; check current CRA publications.</p>
            <p>By using these tools, you acknowledge that results are for informational purposes only. Larry's Help is not responsible for any decisions made based on calculator results.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is the difference between the Income Tax Estimator and the Payroll Calculator?</h3>
              <p>The Income Tax Estimator calculates your total annual tax liability from all income sources (employment, self-employment, investments, capital gains). The Payroll Calculator computes per-pay-period deductions your employer would withhold from each paycheque.</p>
            </div>
            <div className="faq-item">
              <h3>Are these calculations accurate enough for payroll processing?</h3>
              <p>No. These are illustrative estimates for planning purposes. Actual payroll processing must use the official CRA T4127 payroll deduction tables or certified payroll software. Always verify results with your accountant or payroll provider.</p>
            </div>
            <div className="faq-item">
              <h3>What is CPP2 and how does it affect my deductions?</h3>
              <p>CPP2 is the enhanced Canada Pension Plan contribution that started in 2024. It applies an additional 4% on earnings between the YMPE ($68,500) and YAMPE ($73,200). Self-employed individuals pay both the employee and employer portions (8% total on CPP2 earnings).</p>
            </div>
            <div className="faq-item">
              <h3>Does the Payroll Calculator handle all pay frequencies?</h3>
              <p>Yes. We support weekly (52 periods), biweekly (26 periods), semi-monthly (24 periods), and monthly (12 periods) pay cycles. Tax, CPP, and EI are prorated per period based on annualized income.</p>
            </div>
            <div className="faq-item">
              <h3>How does Quebec differ from other provinces?</h3>
              <p>Quebec has its own pension plan (QPP) instead of CPP and files a separate provincial tax return. Quebec also has QPIP (parental insurance) instead of EI parental benefits. Our calculator uses standard CPP/EI rates; Quebecers should be aware of these differences.</p>
            </div>
            <div className="faq-item">
              <h3>Can I use this for self-employment income?</h3>
              <p>Yes. The Income Tax Estimator supports self-employment income with the correct CPP treatment (both employee and employer portions). Self-employed individuals do not pay EI unless they opt in for special benefits.</p>
            </div>
            <div className="faq-item">
              <h3>Are RRSP contributions included?</h3>
              <p>The Income Tax Estimator lets you enter RRSP contributions to see how they reduce your taxable income. RRSP deductions lower your tax at your marginal rate &mdash; a $10,000 contribution at a 40% marginal rate saves $4,000 in tax.</p>
            </div>
            <div className="faq-item">
              <h3>Can I save my calculations?</h3>
              <p>Yes. Create a free account to save scenarios and compare different income situations. This is helpful for salary negotiation, comparing job offers across provinces, or planning RRSP contributions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Tool Recommendations */}
      <section style={{padding: '3rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
          <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <Link to="/rrsp" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Retirement Planner</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Plan your retirement with RRSP, TFSA, CPP, and OAS projections</p>
              </div>
            </Link>
            <Link to="/accounting" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Accounting</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Track income, expenses, and generate financial reports</p>
              </div>
            </Link>
            <Link to="/housing" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Housing Calculator</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Compare renting vs buying with 25-year cost analysis</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Calculate?</h2>
          <p>Estimate your income tax or calculate payroll deductions in seconds.</p>
          <div className="cta-buttons">
            <Link to="/tax-tools" className="btn btn-primary btn-large">Open Tax Tools</Link>
            <Link to="/pricing" className="btn btn-secondary btn-large">See Pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TaxToolsInfoPage;
